import { useCallback, useEffect, useState } from 'react';

const THEME_KEY = 'yorisoi-theme';

/**
 * 初期テーマは常に light。
 * - prefers-color-scheme は初期状態に一切使わない
 * - localStorage が 'dark' のときだけ dark
 * - 未保存・不正値は 'light'
 */
function loadTheme(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const stored = localStorage.getItem(THEME_KEY);
    return stored === 'dark';
  } catch {
    return false;
  }
}

function saveTheme(isDark: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light');
  } catch {
    // ignore (private mode etc.)
  }
}

function applyTheme(isDark: boolean): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.classList.toggle('dark', isDark);
  html.style.colorScheme = isDark ? 'dark' : 'light';
}

export function useTheme(): { isDark: boolean; toggle: () => void } {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const dark = loadTheme();
    // 初回レンダー（ペイント前）に同期で適用し、ダークのちらつきを防ぐ
    applyTheme(dark);
    return dark;
  });

  useEffect(() => {
    applyTheme(isDark);
    saveTheme(isDark);
  }, [isDark]);

  // システムの prefers-color-scheme 変更では切り替えない。
  // dark になるのはユーザーがトグルしたときだけ。

  const toggle = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  return { isDark, toggle };
}
